import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Mail, Link, Share2, Copy, Check } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

interface InviteFriendsViewProps {
  onBack: () => void;
}

export const InviteFriendsView = ({ onBack }: InviteFriendsViewProps) => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();
  
  // Generate a unique referral code based on user ID or username
  const [referralCode, setReferralCode] = useState("");
  const [referralLink, setReferralLink] = useState("");
  
  // Get base URL for sharing
  const baseUrl = window.location.origin;
  
  // Fetch user data and create referral code on component mount
  useEffect(() => {
    const generateReferralCode = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data } = await supabase
          .from('profiles')
          .select('username, handle')
          .eq('id', user.id)
          .single();
          
        if (data) {
          const code = `${data.handle.replace('@', '')}-${Math.random().toString(36).substring(2, 8)}`;
          setReferralCode(code);
          setReferralLink(`${baseUrl}/signup?ref=${code}`);
        }
      }
    };
    
    generateReferralCode();
  }, []);
  
  const handleInvite = async () => {
    try {
      setIsLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast({
          title: "Authentication required",
          description: "Please sign in to invite friends",
          variant: "destructive",
        });
        return;
      }

      const { error } = await supabase
        .from('user_invites')
        .insert({
          inviter_id: user.id,
          invitee_email: email,
        });

      if (error) {
        if (error.message.includes('already exists')) {
          toast({
            title: "Already registered",
            description: "This user is already registered on the platform",
            variant: "destructive",
          });
        } else {
          throw error;
        }
        return;
      }

      toast({
        title: "Invitation sent! 🎉",
        description: "We'll notify you when they join",
      });
      setEmail("");

    } catch (error: any) {
      toast({
        title: "Error sending invite",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleCopyLink = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    
    toast({
      title: "Link copied!",
      description: "Referral link copied to clipboard",
    });
    
    setTimeout(() => setCopied(false), 2000);
  };
  
  const handleShareOnSocial = (platform: string) => {
    let shareUrl = "";
    const text = "Join me on this awesome platform!";
    
    switch (platform) {
      case "twitter":
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(referralLink)}&text=${encodeURIComponent(text)}`;
        break;
      case "facebook":
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralLink)}`;
        break;
      case "whatsapp":
        shareUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(text + " " + referralLink)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, "_blank", "width=600,height=400");
  };
  
  const customMessage = `Hey! I'm inviting you to join this amazing platform. Use my referral code: ${referralCode} or sign up through this link: ${referralLink}`;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon" onClick={onBack}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h3 className="font-semibold">Invite Friends</h3>
      </div>

      <Tabs defaultValue="email" className="w-full">
        <TabsList className="grid grid-cols-3 mb-4">
          <TabsTrigger value="email">
            <Mail className="h-4 w-4 mr-2" />
            Email
          </TabsTrigger>
          <TabsTrigger value="link">
            <Link className="h-4 w-4 mr-2" />
            Referral Link
          </TabsTrigger>
          <TabsTrigger value="social">
            <Share2 className="h-4 w-4 mr-2" />
            Share
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="email" className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Friend's Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="friend@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <Button 
            className="w-full" 
            onClick={handleInvite}
            disabled={!email || isLoading}
          >
            {isLoading ? "Sending..." : "Send Invite"}
          </Button>
          
          <div className="text-sm text-muted-foreground text-center">
            Your friends will receive an email invitation to join the platform
          </div>
        </TabsContent>
        
        <TabsContent value="link" className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="referral-code">Your Unique Referral Code</Label>
            <div className="flex space-x-2">
              <Input
                id="referral-code"
                value={referralCode}
                readOnly
                className="font-mono"
              />
              <Button 
                variant="outline" 
                size="icon"
                onClick={() => {
                  navigator.clipboard.writeText(referralCode);
                  toast({
                    title: "Code copied!",
                    description: "Referral code copied to clipboard",
                  });
                }}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="referral-link">Shareable Link</Label>
            <div className="flex space-x-2">
              <Input
                id="referral-link"
                value={referralLink}
                readOnly
              />
              <Button 
                variant="outline" 
                size="icon"
                onClick={handleCopyLink}
              >
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          </div>
          
          <div className="text-sm text-muted-foreground text-center">
            Share this link or code with friends to invite them to the platform
          </div>
        </TabsContent>
        
        <TabsContent value="social" className="space-y-4">
          <div className="flex justify-center space-x-2 py-2">
            <Button 
              variant="outline" 
              onClick={() => handleShareOnSocial("twitter")}
              className="flex-1"
            >
              <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
              </svg>
              Twitter
            </Button>
            <Button 
              variant="outline" 
              onClick={() => handleShareOnSocial("facebook")}
              className="flex-1"
            >
              <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
              Facebook
            </Button>
            <Button 
              variant="outline" 
              onClick={() => handleShareOnSocial("whatsapp")}
              className="flex-1"
            >
              <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              WhatsApp
            </Button>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="custom-message">Customize Your Message</Label>
            <Textarea 
              id="custom-message" 
              value={customMessage}
              onChange={(e) => {}}
              rows={4}
            />
            <Button 
              className="w-full" 
              variant="outline"
              onClick={() => {
                navigator.clipboard.writeText(customMessage);
                toast({
                  title: "Message copied!",
                  description: "Custom invitation message copied to clipboard",
                });
              }}
            >
              Copy Message
            </Button>
          </div>
          
          <div className="text-sm text-muted-foreground text-center">
            Share a custom message with your invite link on any platform
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};
